import type { Maybe } from '@adornis/base/utilTypes.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { calculateWorkingHoursForEingruppierung } from '../../api/calculateWorkingHours.js';
import { getHistoryForPath } from '../../api/getHistoryForPath.js';
import {
  distributePercent,
  HOURS_EPSILON,
  hoursToPercent,
  isFullyDistributed,
  percentToHours,
  roundHours,
  roundPercent,
} from '../../api/kostenstellenPercent.js';
import type { AppliedContract } from '../../entities/AppliedContract.js';
import { KostenstelleProzentsatz } from '../../entities/vertrag-sektionen/vertrag-eingruppierung/KostenstelleProzentsatz.js';
import type { ContractForm, EingruppierungPath } from './contract-edit/sharedContractEditParts.js';
import { KostenstelleProzentsatzField } from './KostenstelleProzentsatzField.js';

interface KostenstellenEditorProps {
  form: ContractForm;
  contract: Maybe<AppliedContract>;
  path: EingruppierungPath;
  hasEditPermission: boolean;
  /** whether the fields are currently editable (i.e. simple mode locks during a running MAV process) */
  canEdit: boolean;
  onChanged: () => void;
}

const formatHours = (hours: number) => hours.toFixed(2).replace('.', ',');
const formatPercent = (percent: number) => percent.toFixed(2).replace('.', ',');

/**
 * List editor for the kostenstellen of an eingruppierung.
 *
 * The hours a person works are what the user thinks in, the percentage is only the storage format. While the
 * user types, the hours live in this component so the number field is not fought over by the entity, and only
 * on commit are they converted into percentages that add up exactly.
 */
export const KostenstellenEditor = stateful((props: KostenstellenEditorProps) => {
  const eingruppierung = props.form.document?.[props.path];
  const rows = eingruppierung?.agVertragsInfos?.kostenstelleProzentsaetze ?? [];
  const fullHours = eingruppierung ? calculateWorkingHoursForEingruppierung(eingruppierung) : 0;
  const hasFullHours = Number.isFinite(fullHours) && fullHours > 0;

  /**
   * Re-seeds the local hours whenever something outside this component changed them, i.e. selecting another
   * supplement, resetting the form or saving. While the user is typing none of these change, so their input
   * stays untouched.
   */
  const signature = `${fullHours}|${rows.map(row => `${row._id}:${row.prozentsatz ?? ''}`).join('|')}`;
  const [hours, setHours] = useState<number[]>(
    rows.map(row => roundHours(percentToHours(row.prozentsatz ?? 0, fullHours))),
    [signature],
  );

  const assignedHours = hours.reduce((total, value) => total + (Number.isFinite(value) ? value : 0), 0);
  const remainingHours = roundHours(fullHours - assignedHours);
  const complete = isFullyDistributed(assignedHours, fullHours);
  const overAssigned = remainingHours < -HOURS_EPSILON;

  /**
   * The percentages exactly as they would be saved right now. Deriving them per row from the displayed hours
   * instead would show 99,99 % for a complete distribution, because the hours are rounded for display while the
   * full hours of the eingruppierung usually are not a round number.
   */
  const previewPercents = distributePercent(
    hours,
    fullHours,
    rows.map(row => row.prozentsatz),
  );
  const assignedPercent = previewPercents.reduce((total, percent) => total + percent, 0);

  /** writes the hours back as percentages. Only rows that actually moved are touched, so untouched
   * kostenstellen do not show up as changes in the supplement. */
  const commit = (nextHours: number[]) => {
    const percents = distributePercent(
      nextHours,
      fullHours,
      rows.map(row => row.prozentsatz),
    );

    let changed = false;
    percents.forEach((percent, index) => {
      const row = rows[index];
      if (!row) return;
      if (Math.abs((row.prozentsatz ?? 0) - percent) < 0.005) return;
      row.prozentsatz = percent;
      changed = true;
    });

    if (changed) props.onChanged();
  };

  const updateHours = (index: number, value: number) => {
    const next = [...hours];
    next[index] = value;
    setHours(next);
    return next;
  };

  const sumTone = complete ? 'text-success' : overAssigned ? 'text-error' : 'text-warning';
  const progressTone = complete ? 'progress-success' : overAssigned ? 'progress-error' : 'progress-warning';

  return html`
    <div class="flex flex-col gap-2">
      ${
        hasFullHours
          ? html`
              <div class="flex flex-row items-center gap-1 text-sm text-base-content/70">
                <span>Gesamtstunden lt. Vertrag: <b>${formatHours(fullHours)} Std.</b></span>
                <div class="tooltip" ${hoverTooltip()}>
                  <div class="tooltip-content max-w-[420px] text-left">
                    Die Gesamtstunden ergeben sich aus den geleisteten Stunden im Verhältnis zum vollen Deputat bzw. den
                    vollen Stunden der Tätigkeit, hochgerechnet auf eine volle Woche. Werden Ferien eingearbeitet, wird
                    der Wert entsprechend angepasst.
                  </div>
                  <span class="icon-[material-symbols--info-outline] text-base text-base-content/70"></span>
                </div>
              </div>
            `
          : html`
              <p class="text-sm text-warning">
                Für diese Eingruppierung ist noch kein Stundenumfang gepflegt, daher werden die Anteile direkt in
                Prozent erfasst.
              </p>
            `
      }
      ${repeat(
        rows,
        row => row._id,
        (row, index) => html`
          <div class="card card-body gap-2 p-2">
            ${KostenstelleProzentsatzField({
              value: row as KostenstelleProzentsatz,
              disabled: !props.canEdit,
              fullHours,
              hours: hours[index] ?? 0,
              percent: previewPercents[index] ?? 0,
              index,
              singleKostenstelle: rows.length === 1,
              history: getHistoryForPath(
                props.contract?.history,
                `${props.path}.agVertragsInfos.kostenstelleProzentsaetze`,
              ),
              onHoursChanged: value => updateHours(index, value),
              onHoursPicked: value => commit(updateHours(index, value)),
              onPercentPicked: value => {
                row.prozentsatz = value;
                props.onChanged();
              },
              onChanged: () => props.onChanged(),
              onDelete: () => {
                rows.splice(index, 1);
                setHours(hours.filter((_, hourIndex) => hourIndex !== index));
                props.onChanged();
              },
            })}
          </div>
        `,
      )}
      ${
        rows.length > 1 && hasFullHours
          ? html`
              <div class="card card-body gap-1 p-2">
                <div class="flex flex-row flex-wrap items-baseline justify-between gap-2 text-sm">
                  <span>
                    Zugewiesen <b class="${sumTone}">${formatHours(assignedHours)}</b> von
                    <b>${formatHours(fullHours)}</b> Std.
                  </span>
                  <span class="${sumTone}">
                    ${
                      complete
                        ? `Vollständig verteilt (${formatPercent(assignedPercent)} %)`
                        : overAssigned
                          ? `${formatHours(Math.abs(remainingHours))} Std. zu viel verteilt`
                          : `Rest ${formatHours(remainingHours)} Std. (${formatPercent(
                              hoursToPercent(remainingHours, fullHours),
                            )} %)`
                    }
                  </span>
                </div>
                <progress
                  class="progress ${progressTone} w-full"
                  value=${Math.min(Math.max(assignedHours, 0), fullHours)}
                  max=${fullHours}
                ></progress>
              </div>
            `
          : nothing
      }
      ${
        props.hasEditPermission
          ? html`
              <div class="flex flex-row flex-wrap items-center gap-2">
                <button
                  class="btn btn-dash btn-block justify-start flex-1 disabled:pointer-events-none disabled:opacity-50"
                  ?disabled=${!props.canEdit}
                  @click=${() => {
                    const vertragsInfos = props.form.document?.[props.path]?.agVertragsInfos;
                    if (!vertragsInfos) return;

                    vertragsInfos.kostenstelleProzentsaetze ??= [];
                    // the first kostenstelle carries the whole eingruppierung, every further one starts with
                    // whatever is left so the user does not have to do the subtraction themselves
                    const isFirst = !vertragsInfos.kostenstelleProzentsaetze.length;
                    const startHours = isFirst ? fullHours : Math.max(remainingHours, 0);

                    vertragsInfos.kostenstelleProzentsaetze.push(
                      new KostenstelleProzentsatz({
                        kostenstelleID: null,
                        prozentsatz: hasFullHours
                          ? roundPercent(hoursToPercent(startHours, fullHours))
                          : isFirst
                            ? 100
                            : 0,
                      }),
                    );
                    props.onChanged();
                  }}
                >
                  <span class="icon-[material-symbols--add]"></span>
                  <p>Kostenstelle hinzufügen</p>
                </button>

                ${
                  rows.length > 1 && hasFullHours
                    ? html`
                        <button
                          class="btn btn-outline btn-sm"
                          ?disabled=${!props.canEdit || complete}
                          @click=${() => {
                            const lastIndex = rows.length - 1;
                            commit(updateHours(lastIndex, roundHours((hours[lastIndex] ?? 0) + remainingHours)));
                          }}
                        >
                          <span class="icon-[material-symbols--playlist-add-check]"></span>
                          Rest zuweisen
                        </button>
                        <button
                          class="btn btn-outline btn-sm"
                          ?disabled=${!props.canEdit}
                          @click=${() => {
                            const even = rows.map(() => roundHours(fullHours / rows.length));
                            setHours(even);
                            commit(even);
                          }}
                        >
                          <span class="icon-[material-symbols--balance]"></span>
                          Gleichmäßig verteilen
                        </button>
                      `
                    : nothing
                }
              </div>
            `
          : nothing
      }
    </div>
  `;
});
